import { Injectable } from '@angular/core';
import { Subdivision } from './../subdivisions/subdivision';
import { City } from './../cities/city';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { MessagerComponent } from '../messager/messager/messager.component';
import { Customer } from '../customers/customers';

@Injectable({
  providedIn: 'root'
})

export class SubdivisionService {


  private baseURL = environment.baseUrl;
  private subject = new BehaviorSubject<Subdivision[]>([]);

  gridObject$: Observable<Subdivision[]> = this.subject.asObservable();
  isLoading = true;

  constructor(private http: HttpClient, private messager: MessagerComponent) {}

  init() {
    const http$ = this.gridHttp();

    http$.pipe(
     tap(() => {
       this.isLoading = false;
     })
    ).subscribe(
      res => this.subject.next(res.Subdivisions)
    );
  }

  getInitial() {
    return this.gridObject$.pipe(
      map(objects => objects)
    );
  }

  
  getClimateZone(zip: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/subdivisions/getClimateZone/' + zip, {observe: 'body'});
  }

  getCitiesDropdown(name: string): Observable<City[]> {
    return this.http.get<City[]>(this.baseURL + '/rhAPI/city/getDropdownNames/' + name , {observe: 'body'});
  }

  getContractorDropdown(name: string): Observable<Customer[]> {
    return this.http.get<Customer[]>(this.baseURL + '/rhAPI/locations/getContractors/' + name);
  }


  removeTagsRetroactive(sub_id, tags): Observable<any> {  
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/removeTagRetroactive/' + sub_id, {tags: tags}, {observe: 'body'});
  }

  updateTagsRetroactive(sub_id, tags): Observable<any> {  
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/addTagRetroactive/' + sub_id, {tags: tags}, {observe: 'body'});
  }

  deleteSubdivision(id: number): void {

    const list = this.subject.getValue();
    const index  = list.findIndex(object => object.subdivision_id === id);

    const newList = list.slice(0);

    newList.splice(index, 1);
    this.subject.next(newList);

  }

  saveEdit(subdivisionId: number, changes): Observable<any> {

    //console.log(changes);
    const subdivisions = this.subject.getValue();
    const subIndex = subdivisions.findIndex(subdivision => subdivision.subdivision_id === subdivisionId);
    const newSubList = subdivisions.slice(0);

    newSubList[subIndex] = {
      ...subdivisions[subIndex],
      ...changes
    };

    this.subject.next(newSubList);
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/edit/' + subdivisionId, changes).pipe(
      tap((event)=> {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
          this.subject.next(subdivisions);
        } else {
          this.messager.openSnackBar(message, '', 'successAlert');
        }
      })
    );
}


  addSubdivision(changes): Observable<any> {

    //const subdivisions = this.subject.getValue();
    //console.log(changes);

    return this.http.post(this.baseURL + '/rhAPI/subdivisions/create', changes).pipe(
      tap((event) => {
        const message = event.Message as unknown as string;
        if (event.Error) {
          this.messager.openSnackBar(message, 'Close', 'errorAlert');
          //this.subject.next(subdivisions);
        } else {
          this.messager.openSnackBar(message, '', 'successAlert');
        }
      })
    );
  }

  addOneFrontend(changes): void {

    const subdivisions = this.subject.getValue();

    const newSubdivisionList = subdivisions.slice(0);
    newSubdivisionList.unshift(changes);
    this.subject.next(newSubdivisionList);

  }

  getMfContacts(subId: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/subdivisions/mfContacts/' + subId, {observe: 'body'});
  }

  saveMfContacts(subId: number, contacts: any[]): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/mfContacts/' + subId, {contacts}, {observe: 'body'});
  }

  searchMfContacts(name: string, customerId?: number): Observable<any[]> {
    const q = customerId ? '?customer=' + customerId : '';
    return this.http.get<any[]>(this.baseURL + '/rhAPI/contacts/mfSearch/' + encodeURIComponent(name) + q);
  }

  getOneSubdivision(sub_id): Observable<any> {
    return this.http.get<any[]>(this.baseURL + '/rhAPI/subdivisions/edit/' + sub_id, {observe:'body'});
  }

  updateAllSubdivisionNames(sub_id, newName): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/updateNames/' + sub_id,{name: newName} ,{observe:'body'});
  }

  updateAllZips(sub_id, newZip): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/updateZip/' + sub_id,{zip: newZip} ,{observe:'body'});
  }

  updateAceJobs(sub_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/aceProgramUpdate/' + sub_id,{observe:'body'});
  }

  copySubdivision(sub_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/copy/' + sub_id, {},{observe:'body'});
  }

  updateAllGenerator(sub_id): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/updateAllGenerator/' + sub_id,{observe:'body'});
  }

  // Propagate the given subdivision fields ({ column: value }) onto all existing
  // locations in that subdivision. Backend validates fields against an allowlist.
  massUpdateLocations(sub_id, fields): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/massUpdateLocations/' + sub_id, {fields: fields}, {observe:'body'});
  }


  getScopes(): Observable<any> {

    return this.http.get(this.baseURL + '/rhAPI/subdivisions/getScopeList', {observe: 'body'});

  }

  saveNewScope(scope: any): Observable<any> {
  
    return this.http.post(this.baseURL + '/rhAPI/subdivisions/saveScope', {scope: scope.scope, desc: scope.desc, category: scope.category}, {observe: 'body'});

  }

  getSubs(): Observable<any> {

    return this.http.get(this.baseURL + '/rhAPI/subdivisions/getSubs', {observe: 'body'});

  }

  addSub(name: string): Observable<any> {

    return this.http.post(this.baseURL + '/rhAPI/subdivisions/addSub', {name: name}, {observe: 'body'});

  }

  gridHttp(): Observable<any> {
    return this.http.get<Subdivision[]>(this.baseURL + '/rhAPI/subdivisions', {observe: 'body'});
  }
}
